import { useQuery } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import type { Post } from '../types/calendar';
import { useBrands } from './useBrands';
import { logger } from '../utils/logger';
import { normalizeBackendMediaType } from '../utils/tagStyles';

const API_BASE_URL = 'https://api.socialness.ai';

const toYMD = (date: Date): string => date.toISOString().slice(0, 10);

const formatDateParam = (value?: string | Date): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof Date) return toYMD(value);
  return undefined;
};

const convertToISOString = (dateValue: string | Date | undefined): string => {
  if (!dateValue) return '';
  
  if (typeof dateValue === 'string') {
    return dateValue;
  }
  
  if (dateValue instanceof Date) {
    return dateValue.toISOString();
  }
  
  return '';
};

const normalizePost = (post: any): Post => {
  // Handle media conversion - now media is an array
  let media = null;
  
  if (post.media) {
    if (typeof post.media === 'string') {
      // Simple string URL (legacy support)
      media = { url: post.media, type: 'image' as const };
    } else if (Array.isArray(post.media) && post.media.length > 0) {
      // New array format - take the first media item
      const firstMedia = post.media[0];
      
      // Handle Firestore mapValue format
      if (firstMedia?.mapValue?.fields) {
        const fields = firstMedia.mapValue.fields;
        media = {
          url: fields.url?.stringValue || '',
          type: (fields.type?.stringValue?.toLowerCase() === 'video' ? 'video' : 'image') as 'image' | 'video',
          thumbnailUrl: fields.thumbnailUrl?.stringValue
        };
      }
      // Handle standard object format
      else if (firstMedia && firstMedia.url) {
        const mediaTypeFromPost = firstMedia.type === 'video' ? 'video' : 'image';
        media = {
          url: firstMedia.url,
          type: mediaTypeFromPost as 'image' | 'video',
          thumbnailUrl: firstMedia.thumbnailUrl
        };
      }
      // Handle string format in array
      else if (typeof firstMedia === 'string') {
        media = {
          url: firstMedia,
          type: 'image' as const
        };
      }
    } else if (post.media.url) {
      // Legacy object format (backward compatibility)
      const mediaTypeFromPost = post.media.type === 'video' ? 'video' : 'image';
      media = {
        url: post.media.url,
        type: mediaTypeFromPost as 'image' | 'video',
        thumbnailUrl: post.media.thumbnailUrl
      };
    }
  }
  
  // Handle platforms - convert from object format to array format
  let platforms: string[] = [];
  if (post.platforms) {
    if (Array.isArray(post.platforms)) {
      platforms = post.platforms;
    } else if (typeof post.platforms === 'object') {
      // New format: platforms is an object with platform names as keys
      platforms = Object.keys(post.platforms);
    }
  }

  // Build platformResults from the new API structure for backward compatibility
  const platformResults: any = {};
  if (post.platforms && typeof post.platforms === 'object') {
    Object.entries(post.platforms).forEach(([platform, platformData]: [string, any]) => {
      if (platformData?.results) {
        platformResults[platform] = {
          status: platformData.results.status,
          postId: platformData.results.postId,
          publishedAt: platformData.results.publishedAt,
          postUrl: platformData.results.postUrl,
          engagement: platformData.results.engagement || { likes: 0, comments: 0, shares: 0, views: 0 }
        };
      }
    });
  }

  return {
    id: post.id,
    source: post.source || 'socialness',
    hiddenByUser: post.hiddenByUser === true,
    title: post.title || '',
    description: post.description || '',
    content: post.content || '',
    status: post.status || 'draft',
    brandId: post.brandId || '',
    brandName: post.brandName || '',
    brandLogo: post.brandLogo || '',
    platforms,
    scheduledAt: convertToISOString(post.scheduledAt),
    publishedAt: convertToISOString(post.publishedAt),
    reminderAt: convertToISOString(post.reminderAt) || null,
    createdAt: convertToISOString(post.createdAt),
    updatedAt: convertToISOString(post.updatedAt),
    media,
    format: post.format || 'post',
    // Preserve backend-declared media type for UI badges (e.g., "multi-image")
    postMediaType: ((): Post['postMediaType'] => {
      const raw: string | undefined =
        post.postMediaType || post.post_media_type || post.mediaType || post.media_type;
      const normalized = normalizeBackendMediaType(raw);
      if (normalized === 'image' || normalized === 'video' || normalized === 'document' || normalized === 'multi' || normalized === 'text') {
        return normalized;
      }
      // Fallback: infer from media
      if (Array.isArray(post.media) && post.media.length > 1) return 'multi';
      return undefined;
    })(),
    reviewerId: post.reviewerId,
    userId: post.userId,
    engagement: post.engagement || { likes: 0, comments: 0, shares: 0, views: 0 },
    platformResults: platformResults,
    // Preserve platform data with results for more detailed date lookups
    platformData: post.platforms || {}
  };
};

export const usePosts = (params?: { start?: string | Date; end?: string | Date; applanguage?: string; sort?: 'creation-date' | 'update-date' | 'published-date'; order?: 'asc' | 'desc'; fresh?: boolean }) => {
  const { data: brandsData } = useBrands();
  const startParam = formatDateParam(params?.start);
  const endParam = formatDateParam(params?.end);
  const applanguage = params?.applanguage;
  const sort = params?.sort;
  const order = params?.order;
  // ?fresh=true in the URL triggers the backend to re-sync externally-
  // published YouTube videos (see posts/handlers/list.js STEP 5.5). Pages
  // that want that behaviour (My Content, Calendar) forward the param.
  const fresh = params?.fresh === true
    || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('fresh') === 'true');

  return useQuery({
    queryKey: ['posts', { start: startParam || null, end: endParam || null, applanguage: applanguage || null, sort: sort || null, order: order || null, fresh: fresh || null }],
    queryFn: async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('No authenticated user');
        }

        const token = await user.getIdToken();
        const qs = new URLSearchParams();
        if (startParam) qs.set('start', startParam);
        if (endParam) qs.set('end', endParam);
        if (applanguage) qs.set('applanguage', applanguage);
        if (sort) qs.set('sort', sort);
        if (order) qs.set('order', order);
        if (fresh) qs.set('fresh', 'true');
        // Default backend limit is 20 — far too small for My Content / Calendar
        // views that filter by brand/status client-side. Request the backend
        // cap (100) so users with historical external posts actually see them.
        qs.set('limit', '100');

        const url = `${API_BASE_URL}/management/posts${qs.toString() ? `?${qs.toString()}` : ''}`;
        
        // Debug: Log the exact URL and token
        console.log('🔍 API Request:', url);
        console.log('🔑 Token preview:', token.slice(0, 20) + '...');

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Debug: Log the raw response BEFORE processing
        console.log('📦 Raw API Response:', JSON.stringify(data, null, 2));
        console.log('📊 Posts count in response:', data.posts?.length || 0);
        
        // Normalize all posts
        const normalizedPosts = (data.posts || []).map(normalizePost);
        
        return {
          posts: normalizedPosts,
          pagination: data.pagination || {},
          source: 'api'
        };
      } catch (error) {
        logger.error('usePosts', 'Failed to fetch posts', error);
        throw error;
      }
    },
    // Keep posts cached and only refetch when explicitly invalidated (e.g., after updates)
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};